<template>
  <div>
    <PageHeader title="Leads" />
    <SearchToolbar
      class="mb-6"
      :placeholder="$t('Rechercher un lead par nom, prénom, email ou numéro de téléphone')"
      :available-filters="availableFiltersWithOptions"
      :selected-filters="currentFilteringData"
      :initial-keywords="currentFilteringData.keywords"
      :has-active-period="dateRange.some(Boolean)"
      @update-filter-search="updateCurrentFilteringData"
      @update-keyword-search="updateKeywordSearch"
      @clear-filters="clearPeriod"
    >
      <DateRangePicker placeholder="Période" v-model="dateRange" use-query-params />
    </SearchToolbar>
    <Table :columns="columns" :data="formattedData" is-updatable @update="openLeadModal">
      <template #storeSalesChannel="{ row }">
        <TableLink :to="`/stores/${row.storeId}`" data-test="store-link">
          {{ row.storeSalesChannel }}
        </TableLink>
      </template>
    </Table>
    <Pagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      :totalCount="totalCount"
      @changePage="handleChangePage"
    />
    <LeadModal
      :open="openModal"
      :lead="currentLead"
      :discounts="discounts"
      @close="openModal = false"
      @getLeads="getLeads"
    />
  </div>
</template>

<script>
import { DateRangePicker, Pagination, SearchToolbar, Table, PageHeader, TableLink } from "@estaly/ui";
import LeadModal from "@/views/leads/LeadModal.vue";

import { mapActions, mapState } from "vuex";

export default {
  components: { Pagination, PageHeader, Table, DateRangePicker, SearchToolbar, LeadModal, TableLink },

  data() {
    return {
      leads: [],
      currentPage: 1,
      totalPages: null,
      totalCount: null,
      discounts: [],
      filters: {
        callbackDateStatus: [],
        status: [],
        type: [],
        store: [],
        organization: [],
      },
      currentFilteringData: {
        callbackDateStatus: [],
        keywords: null,
        startDate: null,
        endDate: null,
        status: [],
        type: [],
        store: [],
        organization: [],
      },
      columns: [
        { label: "Date de création", field: "creationDate" },
        { label: "Magasin", field: "storeSalesChannel", slot: "storeSalesChannel" },
        { label: "Formule", field: "formula" },
        { label: "Email", field: "email" },
        { label: "Type", field: "type" },
        {
          label: "Statut",
          field: "status",
          class: {
            live: "p-1 rounded-lg bg-yellow-50 text-yellow-600",
            expired: "p-1 rounded-lg bg-gray-50 text-gray-600",
            converted: "p-1 rounded-lg bg-green-50 text-green-600",
            waiting_for_confirmation: "p-1 rounded-lg bg-blue-50 text-blue-600",
            payment_intent_failed: "p-1 rounded-lg bg-red-50 text-red-600",
          },
        },
      ],
      openModal: false,
      currentLead: null,
    };
  },

  computed: {
    ...mapState("backoffice", ["isLoading"]),

    dateRange: {
      get() {
        return [this.currentFilteringData.startDate, this.currentFilteringData.endDate];
      },
      set(value) {
        if (value && value.length === 2) {
          this.updateDateFilters(value[0], value[1]);
        } else {
          this.updateDateFilters(null, null);
        }
      },
    },

    availableFiltersWithOptions() {
      return Object.fromEntries(
        Object.entries(this.filters).filter(([filterKey, filterOptions]) => filterKey && filterOptions?.length > 1),
      );
    },

    formattedData() {
      return this.leads.map((lead) => ({
        id: lead.id,
        creationDate: lead.purchaseDate,
        storeSalesChannel: lead.store.salesChannel,
        email: lead.customer.email,
        name: lead.customer.firstName ? `${lead.customer.firstName} ${lead.customer.lastName}` : null,
        phone: lead.customer.phone,
        productName: lead.variant?.title,
        productPrice: lead.variant?.price,
        expirationDate: lead.expirationDate,
        subscriptionLink: lead.subscriptionLink,
        type: lead.type,
        status: lead.status,
        callbackDate: lead.callbackDate,
        objections: lead.objections,
        comments: lead.comments,
        formula: lead.categoryLabel,
        discount: lead.discount.title,
        seller: lead.seller?.name,
        sellerId: lead.seller?.id,
        storeId: lead.store.id,
      }));
    },
  },

  created() {
    this.getLeads();
  },

  methods: {
    ...mapActions("leads", ["fetchIndex"]),
    openLeadModal(lead) {
      this.currentLead = lead;
      this.openModal = true;
    },
    updateKeywordSearch(keywords) {
      this.currentFilteringData["keywords"] = keywords;
      this.currentPage = 1;
      this.getLeads();
    },
    updateDateFilters(startDate, endDate) {
      this.currentFilteringData["startDate"] = startDate;
      this.currentFilteringData["endDate"] = endDate;
      this.handleChangePage(1);
    },
    clearPeriod() {
      const query = { ...this.$route.query };
      delete query.start_date;
      delete query.end_date;
      this.$router.replace({ query });
      this.dateRange = [null, null];
    },
    updateCurrentFilteringData(filterName, filterOptions) {
      this.currentFilteringData[filterName] = filterOptions;
      this.handleChangePage(1);
    },
    handleChangePage(page) {
      this.currentPage = page;
      this.getLeads();
    },
    async getLeads() {
      const params = {
        page: this.currentPage,
        includes: ["store", "seller", "discount"],
        ...this.currentFilteringData,
      };
      const data = await this.fetchIndex(params);

      if (!data?.page) return;

      this.filters = data.filters;
      this.leads = data.leads;
      this.totalCount = data.page.totalCount;
      this.totalPages = data.page.totalPages;
      this.discounts = [{ Id: null, title: "Aucune" }].concat(data.discounts);
    },
  },
};
</script>
